import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Crown, Medal } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { FollowButton } from "@/components/profile/FollowButton";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface LeaderboardUser {
  id: string;
  display_name: string;
  avatar_url: string;
  is_verified: boolean;
  total_spent?: number;
  total_votes?: number;
}

export const LeaderboardCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topSpenders, setTopSpenders] = useState<LeaderboardUser[]>([]);
  const [topVoters, setTopVoters] = useState<LeaderboardUser[]>([]);

  useEffect(() => {
    loadLeaderboards();
  }, []);

  const loadLeaderboards = async () => {
    // Top spenders
    const { data: spendersData } = await supabase
      .from('wallets')
      .select('user_id, total_spent, profiles(id, display_name, avatar_url, is_verified)')
      .order('total_spent', { ascending: false })
      .limit(10);

    if (spendersData) {
      const spenders = spendersData.map(item => ({
        id: item.user_id,
        display_name: (item.profiles as any)?.display_name || 'Unknown',
        avatar_url: (item.profiles as any)?.avatar_url,
        is_verified: (item.profiles as any)?.is_verified,
        total_spent: item.total_spent || 0
      }));
      setTopSpenders(spenders);
    }

    // Top voters - aggregate vote counts per user
    const { data: votersData } = await supabase
      .from('votes')
      .select('user_id, vote_count, profiles(id, display_name, avatar_url, is_verified)');

    if (votersData) {
      const voterMap = new Map<string, LeaderboardUser & { total_votes: number }>();
      
      votersData.forEach(vote => {
        const existing = voterMap.get(vote.user_id);
        const voteCount = vote.vote_count || 1;
        
        if (existing) {
          existing.total_votes += voteCount;
        } else {
          voterMap.set(vote.user_id, {
            id: vote.user_id,
            display_name: (vote.profiles as any)?.display_name || 'Unknown',
            avatar_url: (vote.profiles as any)?.avatar_url,
            is_verified: (vote.profiles as any)?.is_verified,
            total_votes: voteCount
          });
        }
      });

      const voters = Array.from(voterMap.values())
        .sort((a, b) => b.total_votes - a.total_votes)
        .slice(0, 10);
      
      setTopVoters(voters);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (index === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (index === 2) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="text-sm font-bold text-muted-foreground">#{index + 1}</span>;
  };

  const renderLeaderboard = (users: LeaderboardUser[], showSpent: boolean) => (
    <div className="space-y-2">
      {users.map((leaderboardUser, idx) => (
        <div
          key={leaderboardUser.id}
          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20 cursor-pointer"
          onClick={() => navigate(`/user/${leaderboardUser.id}`)}
        >
          <div className="w-8 flex items-center justify-center flex-shrink-0">
            {getRankIcon(idx)}
          </div>
          <Avatar className="h-9 w-9 border-2 border-primary/10">
            <AvatarImage src={leaderboardUser.avatar_url} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {leaderboardUser.display_name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium truncate">{leaderboardUser.display_name}</p>
              {leaderboardUser.is_verified && (
                <Badge variant="secondary" className="text-xs h-4 px-1">✓</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {showSpent 
                ? `${leaderboardUser.total_spent || 0} coins spent` 
                : `${leaderboardUser.total_votes || 0} votes cast`}
            </p>
          </div>
          {user && leaderboardUser.id !== user.id && (
            <FollowButton userId={leaderboardUser.id} size="sm" variant="outline" />
          )}
        </div>
      ))}
      {users.length === 0 && (
        <p className="text-center py-8 text-sm text-muted-foreground">
          No data yet
        </p>
      )}
    </div>
  );

  return (
    <Card className="clean-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="spenders" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="spenders">Top Fans</TabsTrigger>
            <TabsTrigger value="voters">Most Active</TabsTrigger>
          </TabsList>
          <TabsContent value="spenders" className="mt-0">
            {renderLeaderboard(topSpenders, true)}
          </TabsContent>
          <TabsContent value="voters" className="mt-0">
            {renderLeaderboard(topVoters, false)}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
